<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-image-and-text-section__container">
        <div class="c-image-and-text-section__media-card">
          <img
            v-if="imageSrc"
            class="c-image-and-text-section__image"
            :src="imageSrc"
            :alt="imageAlt"
            loading="lazy"
          />
          <RichText
            v-if="fields.imageOverlayText?.value"
            class="c-image-and-text-section__image-overlay"
            :field="fields.imageOverlayText"
          />
        </div>

        <div class="c-image-and-text-section__content">
          <JssRichText class="c-image-and-text-section__title" :field="fields?.title" tag="h2" />
          <div
            v-if="cards.length"
            class="c-image-and-text-section__cards"
            :class="`c-image-and-text-section__cards--count-${cards.length}`"
          >
            <article
              v-for="(card, index) in cards"
              :key="card.id || index"
              class="c-image-and-text-section__card"
              :class="`c-image-and-text-section__card--${index + 1}`"
            >
              <JssText
                v-if="card.fields?.title?.value"
                class="c-image-and-text-section__card-title"
                :field="card.fields.title"
                tag="h3"
              />
              <RichText
                v-if="card.fields?.description?.value"
                class="c-image-and-text-section__card-description"
                :field="card.fields.description"
              />
              <ul v-if="card.fields?.items?.length" class="c-image-and-text-section__list">
                <li
                  v-for="(item, itemIndex) in card.fields.items"
                  :key="item.id || itemIndex"
                  class="c-image-and-text-section__list-item"
                >
                  <span class="c-image-and-text-section__check" aria-hidden="true"></span>
                  <JssText
                    v-if="item.fields?.text?.value"
                    class="c-image-and-text-section__list-text"
                    :field="item.fields.text"
                    tag="span"
                  />
                </li>
              </ul>
            </article>
          </div>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import { ImageAndTextSectionProps } from '@/types/components/ImageAndTextSection';
import utils from '@/utils';

const props = defineProps<ImageAndTextSectionProps>();

const fields = computed(() => props.fields);
const cards = computed(() => fields.value?.cards ?? []);
const hascheck = computed(() => Boolean(fields.value?.hascheck?.value));

const rootClass = computed(() => {
  const result = utils.overall.baseComClass(props.fields, 'c-image-and-text-section');
  if (hascheck.value) {
    result.push('c-image-and-text-section--lnp-formulation');
  }
  return result;
});

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});

const imageField = computed(() => {
  const image = fields.value?.image as any;
  if (image?.value?.src) return image;
  if (image?.src) return { value: image };
  return image;
});
const imageSrc = computed(() => imageField.value?.value?.src || '');
const imageAlt = computed(() => imageField.value?.value?.alt || '');
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-image-and-text-section {
  background: $white;
  padding: 40px 0;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
  }

  &__media-card {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    min-height: 260px;
    background: $color-light-mercury;
    isolation: isolate;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(12, 29, 23, 0) 42%, rgba(12, 29, 23, 0.76) 100%);
    }
  }

  &__image {
    position: relative;
    z-index: 0;
    display: block;
    width: 100%;
    height: 100%;
    min-height: 260px;
    object-fit: cover;
  }

  &__image-overlay {
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 24px;
    z-index: 2;
    max-width: 460px;
    padding: 0;
    background: transparent;
    color: $white;
    font-size: 14px;
    line-height: 1.5;

    strong,
    b {
      display: block;
      margin: 0 0 8px;
      font-size: 17px;
      line-height: 1.3;
      font-weight: 700;
      letter-spacing: 0;
    }

    p {
      margin: 0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__title {
    margin-bottom: 24px;
  }

  &__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
  }

  &__card {
    border-radius: 12px;
    padding: 24px 22px;
    background: rgba(241, 244, 241, 1);
  }

  &__cards--count-2 &__card--2,
  &__cards--count-3 &__card--3 {
    background: rgba(239, 234, 223, 1);
  }

  &__cards--count-3 &__card--2 {
    background: rgba(239, 245, 232, 1);
  }

  &__card-title {
    margin: 0;
    color: $black;
    font-size: 18px;
    line-height: 1.35;
    font-weight: 700;
  }

  &__card-description {
    margin-top: 12px;
    color: $black;
    font-size: 14px;
    line-height: 1.45;

    p {
      margin: 0;
    }
  }

  &__list {
    display: grid;
    gap: 14px;
    margin: 18px 0 0;
    padding: 0;
    list-style: none;
  }

  &__list-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    color: $black;
    font-size: 14px;
    line-height: 1.45;
  }

  &__check {
    position: relative;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border-radius: 50%;
    background: #6f9877;

    &::after {
      content: '';
      position: absolute;
      left: 6px;
      top: 4px;
      width: 5px;
      height: 8px;
      border: solid $white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  @include tablet {
    padding: 56px 0;

    &__container {
      grid-template-columns: minmax(320px, 0.95fr) minmax(360px, 1fr);
      align-items: center;
      gap: 56px;
    }

    &__media-card {
      min-height: 326px;
    }

    &__image {
      min-height: 326px;
    }

    &__image-overlay {
      left: 56px;
      right: auto;
      bottom: 56px;
      max-width: 460px;
      font-size: 16px;
      line-height: 1.5;

      strong,
      b {
        margin-bottom: 10px;
        font-size: 22px;
        line-height: 1.3;
      }
    }

    &__cards {
      gap: 22px;
    }

    &__card {
      padding: 24px 24px;
    }
  }

  @include tablet-landscape {
    padding: 70px 0;

    &__container {
      gap: 64px;
    }
  }

  &--lnp-formulation {
    .c-image-and-text-section__media-card {
      border-radius: 12px;
      background: #16231c;
    }

    .c-image-and-text-section__image-overlay {
      left: 24px;
      right: 24px;
      bottom: 24px;
      max-width: 330px;
      color: rgba(255, 255, 255, 0.92);
      font-size: 13px;
      line-height: 1.4;
      backdrop-filter: blur(4px);
    }

    .c-image-and-text-section__card {
      padding: 28px 24px;
    }

    @include tablet {
      .c-image-and-text-section__container {
        grid-template-columns: minmax(420px, 1.12fr) minmax(340px, 0.98fr);
        gap: 52px;
      }

      .c-image-and-text-section__media-card,
      .c-image-and-text-section__image {
        min-height: 360px;
      }
    }
  }
}
</style>
